// tricuspidPressure.ts
import type { Editor } from '@tiptap/core';

import { applyChainUpdates, collectChainSnapshot } from './chainFieldSync';
import { CHAIN_FIELDS, recomputeChain } from './measurementChain';
import { CHAIN_EMPTY_MARKER, joinValueAndUnit } from './measurementValueParts';
import { findPhraseOccurrences, type PhraseOccurrence } from './phraseSlotSync';

/**
 * The Tricuspid Valve sentence, wired to the measurement table.
 *
 * It prints the two pressures that the measurement table also carries:
 *
 *   "The estimated right ventricular systolic pressure is 24 mmHg assuming right atrial
 *    pressure of 3 mmHg."
 *
 * RVSP had no anchor of any kind, so the sentence kept whatever the template shipped with
 * while the table showed the real figure. RAP is a dropdown, which is how clinicians pick
 * 3 / 8 / 15, and it was not connected to the chain either — so choosing 8 left the table
 * on 3 and RVSP computed from the wrong pressure.
 *
 * The two are treated differently on purpose:
 *
 *   RVSP — given a `chainField` mark, exactly like the table cell. One key, two
 *   occurrences, so the existing chain keeps them equal and either can be clicked and
 *   retyped.
 *
 *   RAP — left as the dropdown it is, and bridged. Marking it as a chain field too would
 *   put two mechanisms on one span, and the chain popup is checked first in both click
 *   handlers, which would take the dropdown away from the clinician.
 */

/**
 * The RVSP figure and the unit it is printed with.
 *
 * Everything after the anchor is optional, because the clinics' templates disagree:
 *
 *   "… pressure is (RVSP?)mmHg assuming …"   the shipped default: placeholder and unit
 *   "… pressure is 24 mmHg assuming …"       filled in
 *   "… pressure is assuming …"               a placeholder that resolved to nothing, and
 *                                            no unit printed at all — live at Springwood
 *
 * The unit joins the match when it IS printed, because every chained value owns its own
 * unit — the measurement cell holds "23 mmHg", not "23" beside a separate "mmHg" — and a
 * value that did not would gain a second one the first time the chain wrote to it
 * ("28 mmHg mmHg"). An EMPTY value is the other way round: the placeholder carries no
 * unit, because there is no value for one to belong to. Surveyed 14 Sep — all 32
 * production and 31 staging templates store the sentence as `is {{RVSP}} assuming`, with
 * no unit beside the placeholder to strand.
 *
 * The lookahead is what keeps this safe now that the figure is optional. Without it the
 * anchor alone matches, and a template wording the finding qualitatively ("… systolic
 * pressure is normal for age") would have a placeholder pushed into the middle of it.
 * Only the two places a figure genuinely belongs are accepted: before "assuming", and at
 * the end of the sentence.
 *
 * Groups: 1 anchor, 2 the gap after it, 3 the figure, 4 the unit.
 */
const RVSP_SENTENCE =
  /(right ventricular systolic pressure is)(\s*)(\(?\s*RVSP\s*\??\s*\)?|-{2}|\d+(?:\.\d+)?)?(\s*mm\s?Hg)?(?=\s*(?:assuming|[.,)]|$))/i;

/** Text the template leaves behind when the value never resolved. */
const RVSP_UNRESOLVED = /^\(?\s*RVSP\s*\??\s*\)?$/i;

/** What introduces the right atrial pressure dropdown, whatever class the span carries. */
const RAP_LEAD_IN = /right atrial pressure of\s*$/i;

/**
 * Mark the RVSP figure in the Tricuspid Valve sentence so it joins the chain.
 *
 * Matched against each paragraph's whole text rather than node by node. A saved report
 * keeps whatever node split it was serialised with — a figure very often arrives as its
 * own node, with the words before it in one node and the unit in another — and matching
 * inside a single node found nothing on exactly the reports that had been saved once.
 *
 * `fillEmpty` writes a `--` where the figure is missing, the same affordance the empty
 * measurement cells got: with nothing printed there is nothing to click and nowhere for a
 * computed value to go.
 *
 * Returns how many sentences were given a mark.
 */
export function backfillTricuspidPressure(
  editor: Editor,
  options: { fillEmpty?: boolean } = {}
): number {
  if (!editor || !CHAIN_FIELDS.RVSP) {
    return 0;
  }
  const { state } = editor;
  const chainMarkType = state.schema.marks.chainField;
  if (!chainMarkType) {
    return 0;
  }

  const fillEmpty = options.fillEmpty === true;
  const { tr } = state;
  const makeMark = () =>
    chainMarkType.create({
      class: 'RVSP',
      title: CHAIN_FIELDS.RVSP.label,
      'data-chain-warning': null,
    });

  /** Replacements, applied last and back-to-front so earlier positions stay valid. */
  const edits: { from: number; to: number; lead: string; text: string; trail: string }[] = [];
  let marked = 0;

  state.doc.descendants((node, pos) => {
    if (!node.isTextblock) {
      return true;
    }
    // Offsets into `textContent` only map onto document positions while every child is
    // text. A hard break or an inline image occupies a position and contributes no
    // characters, which would shift everything after it.
    let textOnly = true;
    node.forEach(child => {
      if (!child.isText) {
        textOnly = false;
      }
    });
    if (!textOnly) {
      return false;
    }

    const text = node.textContent;
    const found = RVSP_SENTENCE.exec(text);
    if (!found) {
      return false;
    }

    const value = found[3] || '';
    const unit = found[4] || '';
    // +1 for the position of the block's own opening token.
    const base = pos + 1 + found.index + found[1].length + found[2].length;
    const to = base + value.length + unit.length;

    if (value && !RVSP_UNRESOLVED.test(value)) {
      // The unit joins the mark only where the template prints one. Where it does not the
      // figure stands alone, and the chain's own value carries the unit in.
      if (!state.doc.rangeHasMark(base, to, chainMarkType)) {
        tr.addMark(base, to, makeMark());
        marked += 1;
      }
      return false;
    }

    // No figure, or the template's own placeholder — text nobody can use. Replaced by the
    // marker rather than marked as though it were a value.
    if (fillEmpty) {
      const after = text.slice(found.index + found[0].length);
      edits.push({
        from: base,
        to,
        // Separators the sentence needs but the template did not print. UNMARKED and
        // outside the value: the chain replaces the marked text wholesale, so a space
        // inside the mark is gone the first time a figure is written
        // ("is 28 mmHgassuming"), and a marked space renders as an underlined gap.
        lead: found[2] ? '' : ' ',
        // The marker alone, no unit — the same placeholder every other field gets. Nikhil,
        // 14 Sep: "dont include the mmHg when its empty". The unit is not part of a
        // placeholder: `formatChainValue` attaches it to anything the chain computes and
        // `resolveUnit` to anything typed into the popup, so once there is a value the
        // sentence reads exactly as it did before.
        text: CHAIN_EMPTY_MARKER,
        trail: /^[\s.,)]/.test(after) ? '' : ' ',
      });
    }
    return false;
  });

  edits.sort((a, b) => a.from - b.from);
  for (let i = edits.length - 1; i >= 0; i -= 1) {
    const edit = edits[i];
    const content = [state.schema.text(edit.text, [makeMark()])];
    if (edit.lead) {
      content.unshift(state.schema.text(edit.lead));
    }
    if (edit.trail) {
      content.push(state.schema.text(edit.trail));
    }
    tr.replaceWith(edit.from, edit.to, content);
    marked += 1;
  }

  if (marked > 0) {
    tr.setMeta('addToHistory', false);
    editor.view.dispatch(tr);
  }

  return marked;
}

/**
 * The right atrial pressure dropdown in the Tricuspid Valve sentence.
 *
 * Found by the words in front of it rather than by class: the class differs between
 * clinics' templates, the sentence does not.
 */
export function findRapPhraseSlots(editor: Editor): PhraseOccurrence[] {
  if (!editor) {
    return [];
  }
  const { doc } = editor.state;
  return findPhraseOccurrences(editor).filter(occurrence => {
    try {
      const $pos = doc.resolve(occurrence.from);
      return RAP_LEAD_IN.test($pos.parent.textBetween(0, $pos.parentOffset));
    } catch {
      // Unresolvable position — skip it rather than guess.
      return false;
    }
  });
}

/** The first of them, for a caller that only needs to know whether a click landed on one. */
export function findRapPhraseSlot(editor: Editor): PhraseOccurrence | null {
  return findRapPhraseSlots(editor)[0] || null;
}

/** True when `pos` falls inside one of the RAP dropdowns. */
export function isRapPhrasePosition(editor: Editor, pos: number): boolean {
  return findRapPhraseSlots(editor).some(slot => slot.from <= pos && pos < slot.to);
}

/** The figure a RAP dropdown option carries, e.g. "8" or "8 mmHg". Null when it has none. */
export function rapValueFromPhrase(phrase: string): string | null {
  const found = /\d+(?:\.\d+)?/.exec(String(phrase || ''));
  return found ? found[0] : null;
}

/**
 * Write the chain's RAP back into the sentence's dropdown.
 *
 * Only the figure is replaced, so an option reading "8 mmHg" keeps its wording. Returns
 * true when the document changed.
 */
export function syncRapPhrase(editor: Editor, rapValue: string): boolean {
  const figure = rapValueFromPhrase(rapValue);
  if (!editor || !figure) {
    return false;
  }
  const slots = findRapPhraseSlots(editor);
  if (slots.length === 0) {
    return false;
  }

  const { state } = editor;
  const { tr } = state;
  let written = 0;
  // Back-to-front: an earlier position survives a later rewrite of a different length.
  for (let i = slots.length - 1; i >= 0; i -= 1) {
    const slot = slots[i];
    const current = rapValueFromPhrase(slot.text);
    if (current === figure) {
      continue;
    }
    const node = state.doc.nodeAt(slot.from);
    if (!node || !node.isText) {
      continue;
    }
    tr.replaceWith(
      slot.from,
      slot.to,
      state.schema.text(current ? slot.text.replace(current, figure) : figure, node.marks)
    );
    written += 1;
  }

  if (written === 0) {
    return false;
  }
  tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
  return true;
}

/** What the chain did with a pressure picked from the dropdown. */
export type RapPickResult = {
  /** Labels of every value the chain moved, for the notice. */
  changed: string[];
  /** Mid-chain disagreements the recompute raised. Never swallowed. */
  warnings: string[];
};

/**
 * Push a right atrial pressure chosen from the dropdown through the chain.
 *
 * The dropdown is the clinician's edit, so it drives the chain the same way typing into
 * the measurement table does: RAP is written to the table and RVSP recomputed from it and
 * the tricuspid gradient.
 *
 * `baseValues` is the report's own data, layered UNDER the document's values exactly as
 * the two commit paths do it. Without it a gradient that was measured but never printed
 * in the body is invisible, and RVSP silently fails to move.
 */
export function applyRapFromPhrase(
  editor: Editor,
  phrase: string,
  baseValues: Record<string, unknown> = {}
): RapPickResult {
  const none: RapPickResult = { changed: [], warnings: [] };
  const figure = rapValueFromPhrase(phrase);
  if (!editor || !figure) {
    return none;
  }
  const documentValues = collectChainSnapshot(editor);
  const nextValue = joinValueAndUnit(figure, CHAIN_FIELDS.RAP?.unit || '', ' ');
  if (documentValues.RAP === nextValue) {
    return none;
  }

  const previous = { ...baseValues, ...documentValues };
  const { updates, warnings } = recomputeChain('RAP', { ...previous, RAP: nextValue }, previous);
  // RAP itself is written too: the table cell is what the sentence has to agree with, and
  // recomputeChain only returns the values DOWNSTREAM of the edit.
  const withSource = { RAP: nextValue, ...updates };
  applyChainUpdates(editor, withSource);
  return {
    changed: Object.keys(withSource).map(key => CHAIN_FIELDS[key]?.label || key),
    warnings: warnings.map(w => w.message),
  };
}

/**
 * Known limits, deliberate:
 *
 * - The sentence must print the unit ("… is 24 mmHg", "mm Hg" also accepted). A template
 *   that omits it is not matched and simply does not sync, rather than being guessed at.
 * - An option carrying no figure ("unable to estimate") does not write to the table. The
 *   sentence then says something the measurement cell does not, which is honest: there is
 *   no number to record, and inventing one or blanking a measured value would be worse.
 * - A paragraph holding a hard break or an inline image is skipped, because offsets into
 *   its text no longer map onto document positions.
 */
